/**
 * Payment reference rules, shared by the API (which enforces them) and the
 * web Record Payment modal (which mirrors them inline), so the two can never
 * disagree about what a UPI reference is.
 *
 * A UPI reference / UTR / transaction id is what an auditor uses to find a
 * credit on a bank statement. Real ones are usually 12 digits (UPI RRN) but
 * some apps hand out alphanumeric ids, so the rule is deliberately no
 * stricter than "letters, digits, - _ /", 6 to 64 characters.
 */
import { z } from "zod";

export const UPI_REF_MIN_LENGTH = 6;
export const UPI_REF_MAX_LENGTH = 64;
export const UPI_REF_PATTERN = /^[A-Za-z0-9\-_/]+$/;

/**
 * True for any method string that means "paid by UPI": `UPI`, `upi`,
 * `Online/UPI`, `UPI payment`, `MANUAL_UPI`. Word-ish boundary so an unrelated
 * word containing the letters never matches.
 */
export function isUpiMethod(method: string | null | undefined): boolean {
  if (!method) return false;
  return /(^|[^A-Za-z])upi([^A-Za-z]|$)/i.test(method.trim());
}

/** Zod rule for a UPI reference. Trims, then checks length and alphabet. */
export const UpiReferenceSchema = z
  .string({ required_error: "Enter the UPI reference / transaction ID." })
  .trim()
  .min(1, "Enter the UPI reference / transaction ID.")
  .min(UPI_REF_MIN_LENGTH, `UPI reference must be at least ${UPI_REF_MIN_LENGTH} characters.`)
  .max(UPI_REF_MAX_LENGTH, `UPI reference can be at most ${UPI_REF_MAX_LENGTH} characters.`)
  .regex(UPI_REF_PATTERN, "UPI reference can only contain letters, digits, - _ and /.");

/**
 * The problem with `txnId` for this `method`, or null when it is acceptable.
 * Only UPI is constrained: every other method keeps its old behaviour (the
 * reference stays optional and free-form).
 */
export function paymentReferenceProblem(
  method: string | null | undefined,
  txnId: string | null | undefined,
): string | null {
  if (!isUpiMethod(method)) return null;
  const parsed = UpiReferenceSchema.safeParse(txnId ?? "");
  return parsed.success ? null : (parsed.error.errors[0]?.message ?? "Invalid UPI reference.");
}

export type PaymentReferenceKind = "UPI" | "CHEQUE" | "OTHER";

export function paymentReferenceKind(method: string | null | undefined): PaymentReferenceKind {
  if (isUpiMethod(method)) return "UPI";
  if (method && method.trim().toUpperCase() === "CHEQUE") return "CHEQUE";
  return "OTHER";
}

/** Printed / on-screen label for a method's reference number. */
export function paymentReferenceLabel(method: string | null | undefined): string {
  switch (paymentReferenceKind(method)) {
    case "UPI":
      return "UPI Ref / Txn ID";
    case "CHEQUE":
      return "Cheque No.";
    default:
      return "Txn ID";
  }
}

// ── Expenses ────────────────────────────────────────────────────────────────

/** What the Add / Edit Expense form and `POST|PATCH /expenses` say when a UPI expense has no reference. */
export const EXPENSE_UPI_REFERENCE_REQUIRED =
  "UTR / Reference number is required when the payment method is UPI.";

/**
 * True when an expense was paid by UPI: the `UPI` method, or `Other` whose
 * free-text label names UPI ("Google Pay UPI") - otherwise typing "UPI" under
 * Other would sidestep the rule.
 */
export function expenseIsUpi(
  paymentMethod: string | null | undefined,
  customPaymentMethod?: string | null,
): boolean {
  if (isUpiMethod(paymentMethod)) return true;
  return (paymentMethod ?? "").trim().toUpperCase() === "OTHER" && isUpiMethod(customPaymentMethod);
}

/** Bank Transfer / Cheque expenses carry a reference too (owner, 2026-10-05). */
export const EXPENSE_BANK_REFERENCE_REQUIRED =
  "UTR / Transaction ID is required when the payment method is Bank Transfer.";
export const EXPENSE_CHEQUE_NUMBER_REQUIRED =
  "Cheque number is required when the payment method is Cheque.";
/** Indian (CTS-2010) cheques print a 6-digit cheque number. */
export const CHEQUE_NUMBER_PATTERN = /^\d{6}$/;

/** Which kind of reference an expense's payment method demands, or null when it stays optional. */
export type ExpenseReferenceKind = "UPI" | "BANK_TRANSFER" | "CHEQUE";
export function expenseReferenceKind(
  paymentMethod: string | null | undefined,
  customPaymentMethod?: string | null,
): ExpenseReferenceKind | null {
  if (expenseIsUpi(paymentMethod, customPaymentMethod)) return "UPI";
  const m = (paymentMethod ?? "").trim().toUpperCase();
  if (m === "BANK_TRANSFER") return "BANK_TRANSFER";
  if (m === "CHECK" || m === "CHEQUE") return "CHEQUE";
  return null;
}

/**
 * The expense reference rule, as one Zod schema shared by the API (which
 * enforces it on create and edit) and the web form (which mirrors it inline).
 * UPI -> a valid UPI reference (UTR), Bank Transfer -> a bank UTR / transaction
 * id (same alphabet and 6-64 length), Cheque -> the 6-digit cheque number; all
 * REQUIRED (non-blank after trim). Every other method -> optional, free-form.
 */
export const ExpenseReferenceSchema = z
  .object({
    paymentMethod: z.string().nullish(),
    customPaymentMethod: z.string().nullish(),
    referenceNumber: z.string().nullish(),
  })
  .superRefine((v, ctx) => {
    const kind = expenseReferenceKind(v.paymentMethod, v.customPaymentMethod);
    if (!kind) return;
    const issue = (message: string) =>
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["referenceNumber"], message });
    const reference = (v.referenceNumber ?? "").trim();
    if (kind === "UPI") {
      if (!reference) return issue(EXPENSE_UPI_REFERENCE_REQUIRED);
      const problem = paymentReferenceProblem("UPI", reference);
      if (problem) issue(problem);
      return;
    }
    if (kind === "BANK_TRANSFER") {
      if (!reference) return issue(EXPENSE_BANK_REFERENCE_REQUIRED);
      if (reference.length < UPI_REF_MIN_LENGTH) return issue(`UTR / Transaction ID must be at least ${UPI_REF_MIN_LENGTH} characters.`);
      if (reference.length > UPI_REF_MAX_LENGTH) return issue(`UTR / Transaction ID can be at most ${UPI_REF_MAX_LENGTH} characters.`);
      if (!UPI_REF_PATTERN.test(reference)) issue("UTR / Transaction ID can only contain letters, digits, - _ and /.");
      return;
    }
    if (!reference) return issue(EXPENSE_CHEQUE_NUMBER_REQUIRED);
    if (!CHEQUE_NUMBER_PATTERN.test(reference)) issue("Cheque number is the 6-digit number printed on the cheque.");
  });

/** @deprecated name kept for existing imports — the rule now covers Bank Transfer and Cheque too. */
export const ExpenseUpiReferenceSchema = ExpenseReferenceSchema;

/** The problem with this expense's reference for its payment method, or null when it is fine. */
export function expenseReferenceProblem(input: {
  paymentMethod: string | null | undefined;
  customPaymentMethod?: string | null;
  referenceNumber?: string | null;
}): string | null {
  const parsed = ExpenseReferenceSchema.safeParse(input);
  return parsed.success ? null : (parsed.error.errors[0]?.message ?? EXPENSE_UPI_REFERENCE_REQUIRED);
}
